import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Loader2, FileWarning } from "lucide-react";
import axios from "axios";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = {
  abgelaufen: "Dieser Download-Link ist abgelaufen (7 Tage).",
  limit: "Download-Limit erreicht (3 Downloads).",
  unbekannt: "Diese Akte ist uns unbekannt.",
};

export default function DownloadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/download/${token}/info`)
      .then((res) => setInfo(res.data))
      .catch(() => setInfo({ valid: false, reason: "unbekannt" }));
  }, [token]);

  useEffect(() => {
    if (info?.valid && !started) {
      setStarted(true);
      const t = setTimeout(() => {
        window.location.href = `${API}/download/${token}/file`;
      }, 1800);
      return () => clearTimeout(t);
    }
  }, [info, started, token]);

  const expires = info?.expires_at
    ? new Date(info.expires_at).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  return (
    <div className="min-h-screen bg-void text-white flex items-center justify-center px-4 hero-grid">
      <Seo title="Download · SCHATTENPSYCHE" siteName="SCHATTENPSYCHE" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="glass rounded-2xl p-10 max-w-md w-full text-center"
        data-testid="download-page"
      >
        <img src="/logo.png" alt="Schattenpsyche Logo" className="w-16 h-16 rounded-full mx-auto mb-6 ring-1 ring-white/20" />
        {!info && (
          <>
            <Loader2 className="w-10 h-10 text-white animate-spin mx-auto mb-4" />
            <h1 className="font-cinzel text-2xl font-bold">Akte wird geöffnet…</h1>
          </>
        )}
        {info?.valid && (
          <>
            <span className="stamp text-xs mb-4">Freigegeben</span>
            <h1 className="font-cinzel text-2xl font-bold mt-4" data-testid="download-heading">
              Dein Download startet
            </h1>
            <p className="text-slate-400 text-sm mt-3">
              {info.title} wird automatisch heruntergeladen. Falls nicht:
            </p>
            <a
              href={`${API}/download/${token}/file`}
              data-testid="download-file-button"
              className="glow-btn mt-6 !py-3 text-sm"
            >
              <Download className="w-4 h-4" />
              Download erneut starten
            </a>
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-6" data-testid="download-limits">
              Noch {info.downloads_left} von 3 Downloads · gültig bis {expires}
            </p>
          </>
        )}
        {info && !info.valid && (
          <>
            <FileWarning className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <span className="stamp text-xs">Zugriff verweigert</span>
            <h1 className="font-cinzel text-2xl font-bold mt-4" data-testid="download-error-heading">
              Akte geschlossen
            </h1>
            <p className="text-slate-400 text-sm mt-3">
              {REASONS[info.reason] || REASONS.unbekannt} Antworte einfach auf deine Bestell-E-Mail, wir helfen dir.
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="download-home-link"
          className="inline-block mt-8 font-mono text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-white transition-colors"
        >
          Zurück zum Archiv
        </Link>
      </motion.div>
    </div>
  );
}
